
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format, formatDistanceToNow, parseISO } from "date-fns";
import { useVerificationData } from "@/hooks/useVerificationData";
import { 
  AlertCircle, 
  CheckCircle2, 
  Clock, 
  Info, 
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { VerificationStep } from "@/types/verificationTypes";
import { DirectSmtpTest } from "./DirectSmtpTest";

export const EmailVerificationTab: React.FC = () => {
  const { useSessionsQuery, useSessionStepsQuery } = useVerificationData(true);
  
  // Get all sessions
  const { 
    data: sessionsData, 
    isLoading: isLoadingSessions, 
    error: sessionsError, 
    refetch: refetchSessions 
  } = useSessionsQuery("all");
  
  // Get sessions sorted and separated
  const sessions = sessionsData?.sessions || [];
  const latestSession = sessions.length > 0 ? sessions[0] : null;
  const otherSessions = sessions.length > 1 ? sessions.slice(1) : [];
  
  // Get steps for the latest session
  const { 
    data: latestSteps, 
    isLoading: isLoadingLatestSteps 
  } = useSessionStepsQuery(latestSession?.session_id || null);
  
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "success":
        return "bg-green-500 text-white";
      case "error":
        return "bg-red-500 text-white";
      case "warning":
        return "bg-yellow-500 text-white";
      default:
        return "bg-blue-500 text-white";
    }
  };
  
  const getDuration = (startTime: string, endTime: string) => {
    try {
      const start = new Date(startTime).getTime();
      const end = new Date(endTime).getTime();
      const durationMs = end - start;
      
      if (durationMs < 1000) {
        return `${durationMs}ms`;
      } else if (durationMs < 60000) {
        return `${Math.round(durationMs / 1000)}s`;
      } else {
        return `${Math.round(durationMs / 60000)}m ${Math.round((durationMs % 60000) / 1000)}s`;
      }
    } catch (error) {
      return 'N/A';
    }
  };
  
  const renderSteps = (steps: VerificationStep[] | undefined) => {
    if (!steps || steps.length === 0) {
      return (
        <div className="py-4 text-center text-gray-500">
          No steps recorded for this session
        </div>
      );
    }
    
    return (
      <div className="space-y-2">
        {steps.map((step) => (
          <div 
            key={step.id} 
            className="border-l-4 pl-3 py-2"
            style={{ 
              borderLeftColor: 
                step.status === "success" ? "#10b981" : 
                step.status === "error" ? "#ef4444" : 
                step.status === "warning" ? "#f59e0b" : "#3b82f6" 
            }}
          >
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center">
                {getStatusIcon(step.status)}
                <span className="ml-2 font-medium">{step.step}</span>
              </div>
              <div className="flex items-center">
                <Badge className={getStatusColor(step.status)}>
                  {step.status.charAt(0).toUpperCase() + step.status.slice(1)}
                </Badge>
                <span className="text-xs text-gray-500 ml-2">
                  <Clock className="inline-block w-3 h-3 mr-1" />
                  {format(parseISO(step.timestamp), "h:mm:ss a")}
                </span>
              </div>
            </div>
            {step.details && (
              <div className="mt-1 text-xs text-gray-600 bg-gray-50 p-2 rounded">
                {typeof step.details === 'object' ? (
                  <pre className="whitespace-pre-wrap break-words max-w-full">
                    {JSON.stringify(step.details, null, 2)}
                  </pre>
                ) : (
                  step.details
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };
  
  if (isLoadingSessions) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Loading verification data...</span>
      </div>
    );
  }
  
  if (sessionsError) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-red-500">
        <AlertCircle className="h-8 w-8 mb-2" />
        <p className="font-medium">Error loading verification data</p>
        <p className="text-sm">{(sessionsError as Error).message}</p>
        <Button 
          onClick={() => refetchSessions()} 
          variant="outline" 
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* SMTP Direct Test Card */}
      <Card>
        <CardHeader>
          <CardTitle>Direct SMTP Test</CardTitle>
          <CardDescription>
            Test email sending using Supabase edge secrets configuration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DirectSmtpTest />
        </CardContent>
      </Card>
      
      {/* Latest Session Card */}
      <Card>
        <CardHeader>
          <CardTitle>Latest Verification Session</CardTitle>
          <CardDescription>
            Details of the most recent email verification process
          </CardDescription>
        </CardHeader>
        <CardContent>
          {latestSession ? (
            <div>
              <div className="mb-4">
                <div className={`p-4 rounded-md ${
                  latestSession.success 
                    ? "bg-green-50 border border-green-100" 
                    : "bg-gray-50 border border-gray-100"
                }`}>
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                    <div>
                      <p className="font-medium">{latestSession.user_email || "Anonymous"}</p>
                      <p className="text-xs text-gray-500">
                        ID: {latestSession.session_id.substring(0, 8)}... | 
                        Type: {latestSession.type || "Unknown"} | 
                        Steps: {latestSession.step_count} | 
                        Duration: {getDuration(latestSession.start_timestamp, latestSession.last_timestamp)}
                      </p>
                      {latestSession.label && (
                        <p className="text-sm font-medium mt-1 text-blue-600">{latestSession.label}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={latestSession.success ? "bg-green-500" : "bg-red-500"}>
                        {latestSession.success ? "Success" : "Failed"}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        <Clock className="inline-block w-3 h-3 mr-1" />
                        {format(parseISO(latestSession.start_timestamp), "MMM d, h:mm a")}
                        <span className="ml-1 text-gray-400">
                          ({formatDistanceToNow(parseISO(latestSession.start_timestamp), { addSuffix: true })})
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <h3 className="text-lg font-medium mb-2">Session Steps</h3>
              {isLoadingLatestSteps ? (
                <div className="flex justify-center items-center p-4">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading steps...</span>
                </div>
              ) : (
                <ScrollArea className="h-[300px]">
                  {renderSteps(latestSteps)}
                </ScrollArea>
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <p className="text-gray-500 font-medium">No verification sessions found</p>
              <p className="text-sm text-gray-400 mt-1">Complete an authentication flow to see sessions here</p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Session History Card */}
      <Card>
        <CardHeader>
          <CardTitle>Session History</CardTitle>
          <CardDescription>
            Previous email verification sessions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {otherSessions.length > 0 ? (
            <ScrollArea className="h-[400px]">
              <div className="space-y-4">
                {otherSessions.map((session) => (
                  <div 
                    key={session.session_id}
                    className={`p-4 rounded-md border ${
                      session.success ? "border-l-4 border-l-green-500" : "border-l-4 border-l-red-500"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mb-2">
                      <div>
                        <p className="font-medium">{session.user_email || "Anonymous"}</p>
                        <p className="text-xs text-gray-500">
                          ID: {session.session_id.substring(0, 8)}... | 
                          Type: {session.type || "Unknown"} | 
                          Steps: {session.step_count} | 
                          Duration: {getDuration(session.start_timestamp, session.last_timestamp)}
                        </p>
                        {session.label && (
                          <p className="text-sm font-medium mt-1 text-blue-600">{session.label}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={session.success ? "bg-green-500" : "bg-red-500"}>
                          {session.success ? "Success" : "Failed"}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          <Clock className="inline-block w-3 h-3 mr-1" />
                          {format(parseISO(session.start_timestamp), "MMM d, h:mm a")}
                          <span className="ml-1 text-gray-400">
                            ({formatDistanceToNow(parseISO(session.start_timestamp), { addSuffix: true })})
                          </span>
                        </span>
                      </div>
                    </div>
                    
                    <SessionSteps sessionId={session.session_id} />
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="py-8 text-center">
              <p className="text-gray-500">No previous sessions found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Separate component to load steps for individual sessions in history
const SessionSteps: React.FC<{ sessionId: string }> = ({ sessionId }) => {
  const { useSessionStepsQuery } = useVerificationData();
  const [expanded, setExpanded] = React.useState(false);
  
  const { 
    data: steps, 
    isLoading,
    isFetching
  } = useSessionStepsQuery(expanded ? sessionId : null);
  
  if (!expanded) {
    return (
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setExpanded(true)}
        className="text-xs"
      >
        Show Steps
      </Button>
    );
  }
  
  if (isLoading || isFetching) {
    return (
      <div className="p-2 flex items-center">
        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
        <span className="text-xs text-gray-500">Loading steps...</span>
      </div>
    );
  }
  
  return (
    <div className="mt-2 border-t pt-2">
      <div className="flex justify-between items-center mb-2">
        <h4 className="text-sm font-medium">Session Steps</h4>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setExpanded(false)}
          className="text-xs"
        >
          Hide
        </Button>
      </div>
      {steps && steps.length > 0 ? (
        <div className="pl-2 space-y-2">
          {steps.map((step) => (
            <div 
              key={step.id} 
              className="border-l-2 pl-2 py-1"
              style={{ 
                borderLeftColor: 
                  step.status === "success" ? "#10b981" : 
                  step.status === "error" ? "#ef4444" : 
                  step.status === "warning" ? "#f59e0b" : "#3b82f6" 
              }}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <span className="text-xs font-medium">{step.step}</span>
                </div>
                <Badge className={`text-xs ${
                  step.status === "success" ? "bg-green-500" : 
                  step.status === "error" ? "bg-red-500" : 
                  step.status === "warning" ? "bg-yellow-500" : "bg-blue-500"
                }`} variant="secondary">
                  {step.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-500 text-center py-2">No steps recorded</p>
      )}
    </div>
  );
};
